/**
 * The ONE phone rule on the web (R11 PHONE + C5-WEB), mirroring the backend's
 * `core/normalise.py` / `core/phone_write.py` over the same vectors (`contracts/phone_vectors.json`,
 * pinned in `lib/__tests__/phone.test.js`).
 *
 * Three kinds:
 *   - `mobile`  — a person's handset: 10 digits starting 6-9, with an optional +91 / 91 / 0.
 *   - `listing` — a shop, event or job line: a mobile, or a landline with its STD code.
 *   - `contact` — an important contact: a listing number, an emergency short code (112, 108, …)
 *                 or a 1800 / 1860 toll-free number. ONLY contacts take those (owner decision D-6).
 * Mobiles and landlines are stored E.164 (`+91` + 10 digits); short codes and toll-free numbers as
 * their bare digits. Letters are always refused — never silently dropped.
 *
 * The link builders (`telHref`, `waHref`, `smsHref`) share that parse, so a junk value ("12345",
 * "12ab") returns `null` and the caller HIDES the button instead of linking `tel:12345`, and a bare
 * 10-digit legacy WhatsApp number gets its `91` instead of opening a wrong `wa.me/98…` chat.
 * `src/lib/__tests__/phoneLinks.guard.test.js` fails on a hand-built `tel:${…}` / `wa.me/${…}` /
 * `sms:${…}` anywhere else.
 */

export const PHONE_KINDS = ["mobile", "listing", "contact"];

/** Emergency and civic helplines (D-6). Exact strings; the backend's `EMERGENCY_SHORT_CODES`. */
export const EMERGENCY_SHORT_CODES = new Set([
  "100", "101", "102", "104", "108", "112", "181",
  "1033", "1070", "1076", "1077", "1090", "1091", "1098",
  "1912", "1930", "14567",
]);

export const TOLL_FREE_PREFIXES = ["1800", "1860"];
const TOLL_FREE = /^(?:1800|1860)[0-9]{6,7}$/;
const SEPARATORS = /[ \t\-().]/g;
const COMPACT = /^\+?[0-9]+$/;

/** i18n key per refusal (EN + HI in `i18n/{en,hi}.js` under `phone`). */
export const PHONE_ERROR_KEYS = {
  letters: "phone.lettersNotAllowed",
  short_code: "phone.shortCodeContactsOnly",
  mobile: "phone.invalidMobile",
  listing: "phone.invalidListing",
  contact: "phone.invalidContact",
};

function compactOf(raw) {
  const compact = String(raw ?? "").trim().replace(SEPARATORS, "");
  return COMPACT.test(compact) ? compact : null;
}

function nationalNumber(compact) {
  let digits = compact.replace(/^\+/, "").replace(/^0+/, "");
  if (digits.length === 12 && digits.startsWith("91")) digits = digits.slice(2);
  return digits;
}

function isShortOrTollFree(compact) {
  return EMERGENCY_SHORT_CODES.has(compact) || TOLL_FREE.test(compact);
}

/**
 * `validatePhone(raw, kind)` -> `{ ok: true, value }` (value = what the server stores; `""` for a
 * blank input, which is the form's own required-ness rule to refuse) or
 * `{ ok: false, error, messageKey }` where `error` is `letters` | `short_code` | `invalid`.
 */
export function validatePhone(raw, kind = "mobile") {
  if (!PHONE_KINDS.includes(kind)) throw new Error(`unknown phone kind ${kind}`);
  const text = String(raw ?? "").trim();
  if (!text) return { ok: true, value: "" };
  const compact = compactOf(text);
  const refuse = (error) => ({
    ok: false,
    error,
    messageKey: PHONE_ERROR_KEYS[error === "invalid" ? kind : error],
  });
  if (compact === null) return refuse(/[A-Za-z]/.test(text) ? "letters" : "invalid");
  if (kind === "contact" && isShortOrTollFree(compact)) return { ok: true, value: compact };
  const nsn = nationalNumber(compact);
  if (kind === "mobile") {
    if (nsn.length === 10 && /^[6-9]/.test(nsn)) return { ok: true, value: `+91${nsn}` };
  } else if (nsn.length === 10 && nsn[0] !== "0" && !TOLL_FREE_PREFIXES.some((p) => nsn.startsWith(p))) {
    return { ok: true, value: `+91${nsn}` };
  }
  return refuse(isShortOrTollFree(compact) ? "short_code" : "invalid");
}

/** The stored/dialable form of `phone` under `kind`, or `null` (blank or invalid). */
function dialable(phone, kind) {
  const result = validatePhone(phone, kind);
  return result.ok && result.value ? result.value : null;
}

/** `tel:` link — keeps the `+` and E.164; a short code or toll-free number as is; `null` when the
 * value is blank or not a phone (hide the Call button). */
export function telHref(phone) {
  const value = dialable(phone, "contact");
  return value ? `tel:${value}` : null;
}

/** `https://wa.me/…` link: digits only, always with the `91` country code. `null` for a blank or
 * invalid number, a short code or a toll-free line (none of them have WhatsApp). With no phone but a
 * `text`, the generic "share on WhatsApp" link (`wa.me/?text=`). */
export function waHref(phone, text) {
  const query = text ? `?text=${encodeURIComponent(text)}` : "";
  if (phone === null || phone === undefined || String(phone).trim() === "") {
    return text ? `https://wa.me/${query}` : null;
  }
  const value = dialable(phone, "listing");
  return value ? `https://wa.me/${value.slice(1)}${query}` : null;
}

/** `sms:` link, same number rule as `telHref`. With no phone but a `body`, an SMS with no recipient
 * chosen yet (`sms:?body=`). */
export function smsHref(phone, body) {
  const query = body ? `?body=${encodeURIComponent(body)}` : "";
  if (phone === null || phone === undefined || String(phone).trim() === "") {
    return body ? `sms:${query}` : null;
  }
  const value = dialable(phone, "contact");
  return value ? `sms:${value}${query}` : null;
}

/** A contact number as people read it: "9000000001" / "+919000000001" -> "+91 90000 00001". Short
 * codes (112, 108, 1098) and anything unrecognised are returned as typed - never guessed at.
 * (Moved here from `components/operator/ui/display.js`, which re-exports it.) */
export function displayPhone(phone) {
  const raw = String(phone ?? "").trim();
  const digits = raw.replace(/\D/g, "");
  if (/^\d{3,5}$/.test(raw)) return raw;
  // Mobiles start 6-9; a 10-digit string with a leading 0 is a landline (STD code) - left as typed.
  const mobile = (d) => (/^[6-9]\d{9}$/.test(d) ? d : "");
  const ten = digits.length === 12 && digits.startsWith("91") ? mobile(digits.slice(2)) : mobile(digits);
  return ten ? `+91 ${ten.slice(0, 5)} ${ten.slice(5)}` : raw;
}
